import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Row, Spin } from 'antd';

import Truncate from 'components/Truncate';
import { formatDate } from 'helpers/date';
import { parse } from 'query-string';
import PermissionHide from 'v2/components/Shared/PermissionHide';

import Chat from './components/Chat';
import New from './components/New';
import { useMessageChats } from './useMessageChats';

interface IProps {
  match: any;
  location: any;
  history: any;
}

const Messages: React.FC<IProps> = ({ match, location }) => {
  const [search, setSearch] = useState('');
  const [chatId, setChatId] = useState(0);
  const [modalNew, setModalNew] = useState(false);

  const { chats, isLoading } = useMessageChats();

  useEffect(() => {
    const lastChatId = chats[0]?.id || 0;

    if (lastChatId !== chatId && chatId === 0) {
      setChatId(lastChatId);
    }
  }, [chats, chatId, setChatId]);

  const handleSearch = useCallback((event: any) => {
    const value = event.target.value;
    setSearch(value);

    const filter = value.toLowerCase();
    const elements: any = document.getElementsByClassName('--search-chat');

    for (let i = 0; i < elements.length; i++) {
      elements[i].style.display = elements[i].innerText.toLowerCase().includes(filter) ? 'flex' : 'none';
    }
  }, []);

  const resetSearch = useCallback(() => {
    setSearch('');
    const elements: any = document.getElementsByClassName('--search-chat');

    for (let i = 0; i < elements.length; i++) {
      elements[i].style.display = 'flex';
    }
  }, []);

  const handleSelectMessage = useCallback(
    async (id: number | undefined) => {
      if (!id) {
        return;
      }

      if (id !== chatId) {
        setChatId(id);
        resetSearch();
      }
    },
    [chatId, resetSearch, setChatId]
  );

  const toggleModalNew = useCallback(() => {
    setModalNew(prev => !prev);
  }, []);

  const urlInitializedRef = useRef<string | null>(null);

  useEffect(() => {
    const params = match.params;
    const queryParams = parse(location.search);
    const affiliateId = queryParams.affiliate ? parseInt(queryParams.affiliate as string) : null;

    const urlKey = `${params.id ?? ''}-${affiliateId ?? ''}`;
    if (urlInitializedRef.current === urlKey) return;

    try {
      const accepts = chats.map((chat: any) => chat.id);

      if (params.id) {
        const parsedId = parseInt(params.id);

        if (accepts.includes(parsedId)) {
          setChatId(parsedId);
          resetSearch();
          urlInitializedRef.current = urlKey;
        }

        return;
      }

      if (affiliateId) {
        const selectedChat = chats.find((chat: any) => chat.outer.id === affiliateId);

        if (selectedChat?.id) {
          setChatId(selectedChat.id);
          resetSearch();
          urlInitializedRef.current = urlKey;
        }
      }
    } catch (error) {
      toast.error('Ocorreu um erro ao tentar carregar mensagens');
    }
  }, [chats, location.search, match.params, resetSearch]);

  return (
    <div className='animate-fadeIn w-full'>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <div className='h-full w-full rounded-sm bg-white shadow-sm'>
            <div className='flex items-center justify-between border-b border-[#ddd] px-4 py-3 text-base font-medium'>
              <div>Conversas</div>
              <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
                <Button type='primary' size='small' onClick={toggleModalNew} icon={<PlusOutlined />}>
                  Nova
                </Button>
              </PermissionHide>
            </div>

            <div className='px-4 py-3'>
              <Input
                prefix={<SearchOutlined className='text-[#bbb]' />}
                placeholder='Buscar conversa...'
                value={search}
                onChange={handleSearch}
                disabled={isLoading}
                allowClear
              />
            </div>

            <div className='max-h-[65vh] min-h-[65vh] w-full overflow-auto'>
              {isLoading && (
                <div className='flex h-full w-full items-center justify-center'>
                  <Spin />
                </div>
              )}

              {!isLoading && chats && chats.length === 0 && (
                <div className='w-full py-6 text-center text-sm text-[#999]'>Nenhuma conversa encontrada.</div>
              )}

              {!isLoading &&
                chats &&
                chats.length > 0 &&
                chats.map((chat: any, index: any) => {
                  return (
                    <div
                      key={'chat-' + index}
                      className={`flex cursor-pointer items-center px-4 py-2 text-sm text-[#333] opacity-80 hover:bg-[#f9f9f9] ${
                        chat.not_read_count > 0 ? 'opacity-100' : ''
                      } --search-chat ${chatId == chat.id ? 'bg-[#f1f1f1] opacity-100 hover:bg-[#f1f1f1]' : ''}`}
                      onClick={() => handleSelectMessage(chat.id)}
                    >
                      <div
                        className={`relative mr-3 h-[42px] w-[42px] rounded-full border border-[#ddd] bg-[#eee] bg-cover bg-center ${
                          chat.not_read_count > 0 ? '[&_span]:bg-[#087922]' : ''
                        }`}
                        style={{
                          backgroundImage: `url(${chat.outer.avatar ? chat.outer.avatar : '/img/avatar-default.png'})`
                        }}
                      >
                        <span className='absolute -top-0.5 left-0 z-[2] h-3 w-3 rounded-full bg-[#ccc]'></span>
                        <div
                          className={`absolute -right-1 -bottom-1 z-[2] h-5 w-5 rounded-full bg-cover bg-center ${
                            chat.outer.qualification ? 'bg-white' : ''
                          }`}
                          style={{
                            backgroundImage: `url(${
                              chat.outer.qualification
                                ? `https://cdn.eduzzcdn.com/badges/${chat.outer.qualification.badge_elite}` +
                                  (chat.outer.qualification.has_plus ? '-plus' : '') +
                                  '.svg'
                                : ''
                            })`
                          }}
                        ></div>
                      </div>
                      <div>
                        <div className='mb-1 block font-medium'>
                          <Truncate count={25} final='...'>
                            {chat.outer.name}
                          </Truncate>
                        </div>
                        <div className='truncate text-sm text-[#666]'>
                          <Truncate count={28} final='...'>
                            {chat.last.content}
                          </Truncate>
                        </div>
                        <div className='text-xs text-[#999]'>
                          {chat.last.created_at && formatDate(chat.last.created_at, 'dd/MM/yyyy H:mm')}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </Col>

        <Col xs={24} md={16}>
          <Chat chatId={chatId} />
        </Col>
      </Row>

      {modalNew && <New active={modalNew} closeModal={toggleModalNew} />}
    </div>
  );
};

export default Messages;
